import React, { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { handleModalKeyNavigation } from './modalKeyNav';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  width?: string | number;
  showCloseButton?: boolean;
  closeOnBackdropClick?: boolean;
  closeOnEscape?: boolean;
  ariaLabel?: string;
  fullscreenOnMobile?: boolean;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  width,
  showCloseButton = true,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  ariaLabel,
  fullscreenOnMobile = true,
}) => {

  const modalRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  // Size mapping
  const maxSizes: Record<string, string> = {
    sm: '400px',
    md: '540px',
    lg: '720px',
    xl: '960px',
    full: 'calc(100vw - 32px)'
  };

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      handleModalKeyNavigation(e, {
        isOpen,
        closeOnEscape,
        onClose,
        container: modalRef.current,
      });
    },
    [isOpen, closeOnEscape, onClose]
  );

  // Auto-focus only when the modal opens — not when onClose/handleKeyDown
  // identity changes on parent re-renders (e.g. controlled inputs updating context).
  useEffect(() => {
    if (!isOpen) return;

    previousActiveElement.current = document.activeElement as HTMLElement;
    document.body.style.overflow = 'hidden';

    const timeoutId = window.setTimeout(() => {
      if (!modalRef.current) return;
      const focusable = modalRef.current.querySelector<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      (focusable ?? modalRef.current).focus();
    }, 50);

    return () => {
      window.clearTimeout(timeoutId);
      document.body.style.overflow = '';
      if (previousActiveElement.current) {
        previousActiveElement.current.focus();
        previousActiveElement.current = null;
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const content = (
    <div
      className={`z-modal-backdrop ${fullscreenOnMobile ? 'z-modal-backdrop--fullscreen-mobile' : ''}`}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.65)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        zIndex: 9999,
        padding: 'calc(12px + var(--z-safe-top)) calc(10px + var(--z-safe-right)) calc(12px + var(--z-safe-bottom)) calc(10px + var(--z-safe-left))',
      }}
      onClick={(e) => {
        if (closeOnBackdropClick && e.target === e.currentTarget) {
          onClose();
        }
      }}
      aria-hidden="false"
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel || (typeof title === 'string' ? title : 'Dialog')}
        aria-labelledby={title ? 'z-modal-title' : undefined}
        aria-describedby={description ? 'z-modal-desc' : undefined}
        tabIndex={-1}
        className={`animate-zfade-in z-modal-panel ${fullscreenOnMobile ? 'z-modal-panel--fullscreen-mobile' : ''}`}
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: width ? (typeof width === 'number' ? `${width}px` : width) : maxSizes[size] || maxSizes.md,
          maxHeight: 'calc(100vh - 48px)',
          backgroundColor: 'var(--z-surface)',
          borderRadius: 'var(--z-radius-lg, 14px)',
          border: 'none',
          boxShadow: 'var(--z-sh-18)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          fontFamily: 'var(--font-sans)',
          color: 'var(--z-text)'
        }}
      >
        {/* Modal Header */}
        {(title || showCloseButton) && (
          <div
            className="z-modal-header"
            style={{
              padding: '16px 20px',
              borderBottom: 'none',
              backgroundColor: 'var(--z-surface-subtle)',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: '12px'
            }}
          >
            <div>
              {title && (
                <h2
                  id="z-modal-title"
                  style={{
                    fontSize: '17px',
                    fontWeight: 600,
                    margin: 0,
                    color: 'var(--z-text)'
                  }}
                >
                  {title}
                </h2>
              )}
              {description && (
                <p
                  id="z-modal-desc"
                  style={{
                    fontSize: '13px',
                    color: 'var(--z-text-3)',
                    marginTop: '4px',
                    marginBottom: 0
                  }}
                >
                  {description}
                </p>
              )}
            </div>
            {showCloseButton && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Cerrar modal"
                className="z-modal-close-btn"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  minWidth: '32px',
                  minHeight: '32px',
                  borderRadius: 'var(--z-radius-sm, 6px)',
                  border: 'none',
                  backgroundColor: 'var(--z-surface-2)',
                  color: 'var(--z-text-2)',
                  cursor: 'pointer',
                  flexShrink: 0
                }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </svg>
              </button>
            )}
          </div>
        )}

        {/* Modal Body */}
        <div
          className="z-modal-body"
          style={{
            padding: '20px',
            overflowY: 'auto',
            flex: 1
          }}
        >
          {children}
        </div>

        {/* Modal Footer */}
        {footer && (
          <div
            className="z-modal-footer"
            style={{
              padding: '14px 20px',
              borderTop: 'none',
              backgroundColor: 'var(--z-surface-subtle)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '10px'
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(content, document.body);
};
